import { differenceInMinutes } from 'date-fns';
import { type EventVideoConference, type EventWebinar, type NodeOrganization, duplicateEventStartTime } from 'models';
import { useMemo } from 'react';
import { getDatePickerDateType } from 'ui';
import type { OrganizationOption } from '../../../components';
import { useFormatTime, useGenerateDefaultHeroImage } from '../../../hooks';
import type { DuplicateEventFormValues } from '../DuplicateEventForm';

type P = {
  event: EventVideoConference | EventWebinar;
  organization?: NodeOrganization;
};

const getOrganizationOption = ({
  organization,
}: {
  organization?: NodeOrganization;
}): OrganizationOption | undefined => {
  if (!organization) {
    return undefined;
  }

  return {
    label: organization.name,
    optionLabel: organization.name,
    originalOrganization: organization,
    value: organization.id,
  };
};

export const useGetInitialValues = ({ event, organization }: P) => {
  const formatTime = useFormatTime();
  const defaultHeroImage = useGenerateDefaultHeroImage();

  return useMemo((): Partial<DuplicateEventFormValues> => {
    const { channel, endTime, html, name, startTime, text, type } = event;
    const duplicateStartDate = duplicateEventStartTime(startTime);

    return {
      duplicateInviteesFromEvent: true,
      duration: differenceInMinutes(endTime, startTime),
      featuredInChannels: channel?.featuredInChannels,
      heroImage: defaultHeroImage,
      htmlDescription: html,
      name,
      organization: getOrganizationOption({ organization }),
      showInChannels: channel?.showInChannel,
      startDate: getDatePickerDateType(duplicateStartDate),
      startTime: formatTime(duplicateStartDate),
      tags: channel?.tags,
      textDescription: text,
      type,
    };
  }, [event, organization, defaultHeroImage, formatTime]);
};
